import isEqual from 'lodash-es/isEqual';
import GetRoomAvailabilityRequest from 'owa-calendar-scheduling-service/lib/schema/GetRoomAvailabilityRequest';
import getRoomAvailabilityService from 'owa-calendar-scheduling-service/lib/services/GetRoomAvailabilityService';
import { formatDate, startOfDay, addDays, OwaDate, timestamp } from 'owa-datetime';
import DateTimeTimeZone from 'owa-calendar-scheduling-service/lib/schema/DateTimeTimeZone';
import { createRecurrenceConstraints } from 'owa-calendar-scheduling-service/lib/utils/createTimeConstraints';
import RecurrenceConstraint from 'owa-calendar-scheduling-service/lib/schema/RecurrenceConstraint';
import { isFeatureEnabled } from 'owa-feature-flags';
import { orchestrator } from 'satcheljs';
import { CalendarEventTime } from '../components/RoomSelector/RoomSelector.types';
import { updateRoomAvailabilityResults, getRoomAvailability } from '../actions/calendarRoomActions';
import {
    RoomRecurrenceAvailability,
    RoomAvailabilityState,
} from '../store/schema/RoomAvailabilityState';
import getRoomAvailabilityStore from '../store/roomAvailabilityStore';
import { FreeBusyType } from 'owa-freebusy-cache';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';

const DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const TwoMinutes = TimeConstants.MillisecondsIn1Min * 2;

/**
 * Calls room availability using GetRoomAvailability API if not already requested. It updates the availability cache with
 * availability: the overall availability.
 * availableCount: the number of available instances
 * totalInstances: the number of instances expanded in the Recurrence
 * isGetingAvailabilityForRooms: true if still waiting for responses, false once completed.
 * @param roomEmail the room resource that need to have availability obtained
 * @param calendarEvent the calendar event that has a recurrence
 **/
export default orchestrator(getRoomAvailability, async actionMessage => {
    const { roomEmail, calendarEventTime } = actionMessage;
    if (!alreadyRequested(roomEmail, calendarEventTime)) {
        const startCorrected: OwaDate = calendarEventTime.IsAllDayEvent
            ? startOfDay(calendarEventTime.Start)
            : calendarEventTime.Start;
        const endCorrected: OwaDate = calendarEventTime.IsAllDayEvent
            ? startOfDay(addDays(calendarEventTime.Start, 1))
            : calendarEventTime.End;

        const recStart: DateTimeTimeZone = {
            DateTime: formatDate(startCorrected, DATE_FORMAT),
            TimeZone: calendarEventTime.Start.tz,
        };

        const recEnd: DateTimeTimeZone = {
            DateTime: formatDate(endCorrected, DATE_FORMAT),
            TimeZone: calendarEventTime.End.tz,
        };

        const recurrenceConstraint: RecurrenceConstraint = calendarEventTime.Recurrence
            ? createRecurrenceConstraints(calendarEventTime.Recurrence, calendarEventTime.Start.tz)
            : null;

        const roomAvailabilityRequest: GetRoomAvailabilityRequest = {
            emailAddress: roomEmail,
            start: recStart,
            end: recEnd,
            recurrence: recurrenceConstraint,
            requiredAttendees: calendarEventTime.RequiredAttendees.map(
                attendee => attendee.Mailbox.EmailAddress
            ),
            optionalAttendees: calendarEventTime.OptionalAttendees.map(
                attendee => attendee.Mailbox.EmailAddress
            ),
            ResolveBookingPolicy: isFeatureEnabled('cal-mf-roomBookingPolicy'),
        };

        await getRoomAvailabilityInitialize(roomAvailabilityRequest, calendarEventTime);
    }
});

async function getRoomAvailabilityInitialize(
    roomAvailabilityRequest: GetRoomAvailabilityRequest,
    requestedCalendarEventTime: CalendarEventTime
) {
    const requestTimeStamp = timestamp();

    // set isGettingAvailabilityForRooms to true to signal the callback is in progress, and set the request timestamp so that
    // we can corrolate the response with the latest request. The other parameteres won't be set until switched to false
    updateRoomAvailabilityResults(
        roomAvailabilityRequest.emailAddress,
        FreeBusyType.Loading /* availability*/,
        null /* roomRecurrenceAvailability*/,
        requestedCalendarEventTime,
        requestTimeStamp /* availabilityTimestamp */
    );

    const getRoomAvailabilityResult = await getRoomAvailabilityService(
        roomAvailabilityRequest,
        'RoomPanel'
    );

    const latestRequestTimeStamp = getRoomAvailabilityStore().roomAvailabilityStates.get(
        roomAvailabilityRequest.emailAddress
    ).availabilityTimestamp;

    // if this request is the latest request made than we update the store. Otherwise there is a newer request that is either pending or completed, so we should ignore the response to this one.
    if (latestRequestTimeStamp == requestTimeStamp) {
        let roomRecurrenceAvailability: RoomRecurrenceAvailability = null;
        let availability: FreeBusyType = FreeBusyType.NoData;

        if (getRoomAvailabilityResult) {
            availability = convertResponseAvailabilityToFreeBusyType(
                getRoomAvailabilityResult.Availability
            );

            roomRecurrenceAvailability = roomAvailabilityRequest.recurrence
                ? {
                      availableInstancesCount: getRoomAvailabilityResult.NumberOfFreeOccurrences,
                      totalInstancesCount: getRoomAvailabilityResult.TotalOccurrences,
                      isRecurrenceExpansionPartial:
                          getRoomAvailabilityResult.IsRecurrenceExpansionPartial,
                  }
                : null;
        }

        updateRoomAvailabilityResults(
            roomAvailabilityRequest.emailAddress,
            availability,
            roomRecurrenceAvailability,
            requestedCalendarEventTime,
            requestTimeStamp
        );
    }
}

function convertResponseAvailabilityToFreeBusyType(availability: string): FreeBusyType {
    if (!availability) {
        return FreeBusyType.NoData;
    }

    if (availability.toLowerCase() == 'free') {
        return FreeBusyType.Free;
    }

    if (availability.toLowerCase() == 'unknown') {
        return FreeBusyType.NoData;
    }

    return FreeBusyType.Busy;
}

function alreadyRequested(roomEmail: string, calendarEventTime: CalendarEventTime): boolean {
    const roomAvailabilityState: RoomAvailabilityState = getRoomAvailabilityStore().roomAvailabilityStates.get(
        roomEmail
    );
    if (!roomAvailabilityState || !roomAvailabilityState.requestedCalendarTime) {
        return false;
    }

    const { availabilityTimestamp, requestedCalendarTime } = roomAvailabilityState;
    const lessThanTwoMinsAgo =
        !availabilityTimestamp || timestamp() - availabilityTimestamp <= TwoMinutes;

    return (
        isEqual(calendarEventTime.Start, requestedCalendarTime.Start) &&
        isEqual(calendarEventTime.End, requestedCalendarTime.End) &&
        calendarEventTime.IsAllDayEvent === requestedCalendarTime.IsAllDayEvent &&
        isEqual(calendarEventTime.Recurrence, requestedCalendarTime.Recurrence) &&
        // Attendees are complex collections that are expensive to compare. Check length as an optimization
        calendarEventTime.RequiredAttendees.length ===
            requestedCalendarTime.RequiredAttendees.length &&
        calendarEventTime.OptionalAttendees.length ===
            requestedCalendarTime.OptionalAttendees.length &&
        lessThanTwoMinsAgo
    );
}
